import React, { FunctionComponent, useCallback } from "react";
import styles from "./Header6.module.scss";
import Header6Props from "./Header6Props.interface";

import setHeaderStyles from "./hooks/setHeaderStyles";

import { CLOSE_ALL_OVERLAYS, OPEN_MENU } from "layout/redux/overlay.actions";
import { useDispatch, useSelector } from "react-redux";
import Button from "components/Button/Button";
import combineClasses from "helpers/combine-classes";
import getImageAspectRatio from "app/media/helpers/getImageAspectRatio";
import ImageViewer from "components/ImageViewer/ImageViewer";
import Anchor from "components/Anchor/Anchor";

import Navigation from "../Header2/components/Navigation/Navigation";
import SocialLinks from "../Header2/components/SocialLinks/SocialLinks";
import useScrollDirection from "./hooks/useScrollDirection";
import useScrollPosition from "./hooks/useScrollPosition";
import useStickyHeader from "./hooks/useStickyHeader";
import MenuOverlay from "../Header2/components/MenuOverlay/MenuOverlay";
import FixrOverlay from "../Header2/components/FixrOverlay/FixrOverlay";

const Header6: FunctionComponent<Header6Props> = (props) => {
    const dispatch = useDispatch();

    let header: any;
    if (props?.theme?.header && props?.theme?.header.length > 0) {
        header = props?.theme?.header?.[0]?.content;
    }

    const { scrollDirection } = useScrollDirection();
    const { scrollPosition } = useScrollPosition();

    const { overThreshold } = useStickyHeader();
    const headerStyles = setHeaderStyles(header);

    const isStickyClass = overThreshold ? styles.is_sticky : undefined;

    const page = useSelector((state) => state.page.content);
    const overlay = useSelector((state) => state.overlay);
    const overlayActiveClass = overlay.currentOverlay === "MENU" ? styles.is_active : undefined;

    const logoTypeClass = header?.logoType ? styles[`logo_type_${header.logoType}`] : undefined;
    const logoImageType = header?.logoType === "Vertical" ? "containX" : "containY";
    const imageAspectRatio = getImageAspectRatio(undefined, header?.logo?.umbracoWidth, header?.logo?.umbracoHeight);

    const hasTwoHeaderButtonsClass = props.headerButtons && props.headerButtons?.length > 1 ? styles.two_header_buttons : "";
    const headerButtonContentType = header?.headerButtons?.[0] && header.headerButtons.length > 0 ? header.headerButtons[0].content._contentType : undefined;
    const isHeaderShownClass = scrollPosition && scrollDirection === "SCROLL_DOWN" ? styles.is_hidden : styles.is_shown;

    const toggleMenu = useCallback(
        (event: any) => {
            if (event) {
                event.stopPropagation();
            }

            if (overlay?.currentOverlay === "MENU") {
                dispatch({
                    type: CLOSE_ALL_OVERLAYS,
                });
            } else {
                dispatch({
                    type: OPEN_MENU,
                });
            }
        },
        [overlay.currentOverlay]
    );

    return (
        <header
            className={combineClasses(styles.wrapper, isStickyClass, isHeaderShownClass)}
            style={headerStyles}
        >
            <div className={combineClasses(styles.inner, logoTypeClass)}>
                {header?.logo ? (
                    <Anchor
                        className={styles.image_link}
                        to={page?._homeNodeUrl}
                    >
                        <ImageViewer
                            className={styles.image}
                            src={header.logo.umbracoFile.src}
                            extensionType="png"
                            type={logoImageType}
                            aspectRatioX={imageAspectRatio.aspectRatioX}
                            aspectRatioY={imageAspectRatio.aspectRatioY}
                        />
                    </Anchor>
                ) : null}

                <div className={styles.content}>
                    <Navigation menu={props.menu} />

                    <SocialLinks {...props} />

                    {/* Buttons */}
                    {props.headerButtons ? (
                        <div className={combineClasses(styles.action_buttons, hasTwoHeaderButtonsClass)}>
                            {props.headerButtons?.map((button, index) => {
                                return (
                                    <Button
                                        key={index}
                                        to={button.content.link}
                                        className={styles.action_button}
                                        contentType={headerButtonContentType}
                                        {...button.content}
                                    >
                                        {button.content.label}
                                    </Button>
                                );
                            })}
                        </div>
                    ) : null}
                </div>

                <button
                    className={combineClasses(styles.menu_toggle_button, overlayActiveClass)}
                    onClick={(event) => toggleMenu(event)}
                >
                    <span></span>
                    <span></span>
                    <span></span>
                </button>
            </div>

            {overlay.currentOverlay === "MENU" ? (
                <MenuOverlay
                    header={header}
                    page={page}
                    {...props}
                />
            ) : null}

            {overlay.currentOverlay === "FIXR_OVERLAY" ? (
                <FixrOverlay
                    {...props}
                    state={overlay.currentOverlay}
                />
            ) : null}
        </header>
    );
};

export default Header6;
